import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  GitBranch,
  Loader2,
  RefreshCw,
  Settings2,
  ShieldCheck,
  Sparkles,
  Ticket,
} from "lucide-react";
import {
  fetchMatchedTickets,
  fetchPotentialMatchPartners,
} from "../../redux/operatorConsoleSlice";
import { formatCash } from "../../utils/operatorConsoleHelpers";

const percentLabel = (fraction) => `${(Number(fraction || 0) * 100).toFixed(1)}%`;

const scoreLabel = (score) => `${Math.round(Number(score || 0) * 100)}`;

const STATUS_TONES = {
  pending: "oc-partners-status-locked",
  won: "oc-partners-status-ready",
  paid: "oc-partners-status-ready",
  lost: "oc-partners-status-ready",
  voided: "oc-partners-status-locked",
  refunded: "oc-partners-status-locked",
};

const OperatorMatchingPanel = ({ bet, operatorUserId, houseId, onOpenLimits }) => {
  const dispatch = useDispatch();
  const {
    potentialPartners,
    potentialPartnersHouseId,
    potentialPartnersStatus,
    potentialPartnersError,
    matchedTickets,
    matchedTicketsHouseId,
    matchedTicketsStatus,
    matchedTicketsError,
  } = useSelector((s) => s.operatorConsole);

  const matchingEnabled = Boolean(bet?.matching?.enabled);
  const maxPerTicket = Number(bet?.matching?.maxPerTicket || 0);
  const maxTotalExposure = Number(bet?.matching?.maxTotalExposure || 0);
  const matchedExposure = Number(bet?.matching?.matchedExposure || 0);

  const loadAll = useCallback(() => {
    if (!houseId || !operatorUserId) return;
    dispatch(fetchPotentialMatchPartners({ houseId, operatorUserId }));
    dispatch(fetchMatchedTickets({ houseId, operatorUserId }));
  }, [dispatch, houseId, operatorUserId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const partnersFresh =
    String(potentialPartnersHouseId) === String(houseId) ? potentialPartners : [];
  const ticketsFresh =
    String(matchedTicketsHouseId) === String(houseId) ? matchedTickets : [];
  const loading =
    potentialPartnersStatus === "loading" || matchedTicketsStatus === "loading";

  return (
    <section className="oc-panel oc-matching-panel">
      <header className="oc-panel-head oc-partners-head">
        <div>
          <p className="oc-loan-eyebrow">Cross-house liquidity</p>
          <h2>House matching</h2>
          <p>
            Partner houses co-fund tickets your balance cannot cover alone —
            profit and risk are shared by contribution.
          </p>
        </div>
        <div className="oc-partners-head-meta">
          <span
            className={`oc-partners-status-pill ${
              matchingEnabled ? "oc-partners-status-ready" : "oc-partners-status-locked"
            }`}
          >
            {matchingEnabled ? "MATCHING ON" : "MATCHING OFF"}
          </span>
          <button
            type="button"
            className="oc-loan-icon-btn"
            onClick={loadAll}
            disabled={loading}
            aria-label="Refresh matching data"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      <div className="oc-stat-grid oc-stat-grid-compact">
        <div className="oc-stat">
          <em>Matched exposure</em>
          <strong className="tabular-nums">
            {formatCash(matchedExposure, { compact: true })}
          </strong>
          <span>Locked on partner tickets</span>
        </div>
        <div className="oc-stat">
          <em>Per-ticket cap</em>
          <strong className="tabular-nums">
            {maxPerTicket > 0 ? formatCash(maxPerTicket, { compact: true }) : "No cap"}
          </strong>
          <span>Max on one matched ticket</span>
        </div>
        <div className="oc-stat">
          <em>Total cap</em>
          <strong className="tabular-nums">
            {maxTotalExposure > 0
              ? formatCash(maxTotalExposure, { compact: true })
              : "No cap"}
          </strong>
          <span>Ceiling across matches</span>
        </div>
        <div className="oc-stat oc-stat-accent">
          <em>Settings</em>
          <button
            type="button"
            className="oc-risk-expand-btn"
            onClick={onOpenLimits}
            style={{ marginTop: 4 }}
          >
            <Settings2 size={14} />
            {matchingEnabled ? "Adjust matching" : "Enable matching"}
          </button>
        </div>
      </div>

      {!matchingEnabled ? (
        <div className="oc-alert oc-risk-alert oc-risk-alert-info">
          <ShieldCheck size={14} />
          <div>
            <strong>Matching is off</strong>
            <p>
              Your balance never funds other houses, and big tickets on your
              house are rejected when your available balance cannot cover them.
              Turn matching on in house limits to join the liquidity network.
            </p>
          </div>
        </div>
      ) : null}

      <div className="oc-panel-subsection">
        <header className="oc-panel-subhead">
          <Sparkles size={15} aria-hidden />
          <div>
            <h3>Potential partners</h3>
            <p>
              Ranked by shared upcoming fixtures, full-time odds risk profile,
              and how often you have matched before. Estimates — the actual
              match happens at ticket time against live balances.
            </p>
          </div>
        </header>

        {potentialPartnersStatus === "loading" ? (
          <div className="oc-loan-loading">
            <Loader2 size={20} className="animate-spin" />
            <span>Scoring partner houses…</span>
          </div>
        ) : potentialPartnersError ? (
          <p className="oc-settings-form-error">{String(potentialPartnersError)}</p>
        ) : partnersFresh.length === 0 ? (
          <p className="oc-panel-empty">
            No opted-in partner houses available right now.
          </p>
        ) : (
          <ul className="oc-risk-outcomes oc-matching-partner-list">
            {partnersFresh.slice(0, 8).map((partner, idx) => (
              <li key={partner.houseId}>
                <div className="oc-risk-outcome-copy">
                  <strong>
                    {idx + 1}. {partner.name}
                  </strong>
                  <span>
                    {partner.metrics?.sharedUpcomingFixtures || 0} shared upcoming
                    {" · "}
                    {partner.metrics?.matchedCount || 0} past match
                    {(partner.metrics?.matchedCount || 0) === 1 ? "" : "es"}
                  </span>
                </div>
                <div className="oc-risk-outcome-side">
                  <em className="tabular-nums">
                    {formatCash(partner.availableCapacity || 0, { compact: true })}
                  </em>
                  <span>Capacity · score {scoreLabel(partner.score)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="oc-panel-subsection">
        <header className="oc-panel-subhead">
          <Ticket size={15} aria-hidden />
          <div>
            <h3>Matched tickets</h3>
            <p>
              Every ticket your balance co-funds — your contribution, your profit
              share, and the houses you are partnering with.
            </p>
          </div>
        </header>

        {matchedTicketsStatus === "loading" ? (
          <div className="oc-loan-loading">
            <Loader2 size={20} className="animate-spin" />
            <span>Loading matched tickets…</span>
          </div>
        ) : matchedTicketsError ? (
          <p className="oc-settings-form-error">{String(matchedTicketsError)}</p>
        ) : ticketsFresh.length === 0 ? (
          <p className="oc-panel-empty">
            <GitBranch size={14} aria-hidden style={{ verticalAlign: "-2px", marginRight: 6 }} />
            No matched tickets yet.
          </p>
        ) : (
          <ul className="oc-risk-outcomes oc-matching-ticket-list">
            {ticketsFresh.map((ticket) => (
              <li key={ticket.ticketId}>
                <div className="oc-risk-outcome-copy">
                  <strong>
                    {ticket.role === "primary"
                      ? "Your house ticket"
                      : `Ticket on ${ticket.primaryHouse?.name || "partner house"}`}
                    {"  "}
                    <span
                      className={`oc-partners-status-pill ${
                        STATUS_TONES[ticket.status] || "oc-partners-status-locked"
                      }`}
                      style={{ marginLeft: 6 }}
                    >
                      {String(ticket.status || "pending").toUpperCase()}
                    </span>
                  </strong>
                  <span>
                    Stake {formatCash(ticket.stake || 0, { compact: true })}
                    {" · max pay "}
                    {formatCash(ticket.potentialPayout || 0, { compact: true })}
                    {ticket.partners?.length
                      ? ` · with ${ticket.partners
                          .map((p) => `${p.name || "house"} (${percentLabel(p.percent)})`)
                          .join(", ")}`
                      : ""}
                  </span>
                </div>
                <div className="oc-risk-outcome-side">
                  <em className="tabular-nums">
                    {formatCash(ticket.myContribution || 0, { compact: true })}
                  </em>
                  <span>Your {percentLabel(ticket.myPercent)} share</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default OperatorMatchingPanel;
